import { FC } from 'react';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import ErrorView from 'components/ErrorView';
import useToast from 'components/ToastProvider/useToast';
import BankAccountForm from 'features/Bank/components/BankAccountForm';
import { BankAccountFormSchema } from 'features/Bank/components/BankAccountForm/bankAccountForm.schema';
import { getBankAccountPath } from 'features/Bank/routes';
import logger from 'helpers/logger';

import { UpdateSecondaryBankAccountDocument } from './graphql/updateSecondaryBankAccount.gql';
import locales from './locales';

interface EditBankAccountProps {
  match: {
    params: {
      bankAccountId: string;
    };
  };
}

const EditBankAccount: FC<EditBankAccountProps> = ({
  match: {
    params: { bankAccountId },
  },
}) => {
  const history = useHistory();
  const { bankAccounts } = useCompanyBankAccounts();

  const currentBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.bankAccountId === bankAccountId,
  );

  const [updateBankAccount, { loading }] = useMutation(
    UpdateSecondaryBankAccountDocument,
  );
  const { showToast } = useToast();

  if (!currentBankAccount) {
    logger.error(
      `[EditBankAccount] Bank account ${bankAccountId} not found in company bank accounts`,
    );

    return <ErrorView />;
  }

  const handleSubmit = async (data: BankAccountFormSchema) => {
    if (!bankAccountId) {
      throw new Error('bankAccountId is missing');
    }

    try {
      const { data: updatedBankAccount } = await updateBankAccount({
        variables: { input: { bankAccountId, ...data } },
      });

      const updatedBankAccountId =
        updatedBankAccount?.updateSecondaryBankAccount?.bankAccountId;

      if (updatedBankAccountId) {
        history.push(getBankAccountPath(updatedBankAccountId));
      }
    } catch (error) {
      showToast({ message: locales.updateError, type: 'error' });
    }
  };

  return (
    <BankAccountForm
      defaultValues={{
        illustrationName: currentBankAccount.illustrationName || '',
        name: currentBankAccount.name,
      }}
      loading={loading}
      onSubmit={handleSubmit}
    />
  );
};

export default EditBankAccount;
